/**
 * NZ banks that support one-off pay-by-bank via Akahu.
 *
 * Official payer banks are ANZ, ASB, BNZ, Kiwibank, NZ Home Loans (NZHL), and
 * Westpac. Prefer live names/logos from the payments connections API; fall back
 * to these names when that call fails or returns nothing useful.
 *
 * @see https://developers.akahu.nz/reference/getconnections
 */
export type AkahuSupportedBank = {
  name: string;
  logoUrl?: string | null;
};

/** Official one-off pay-by-bank banks shown when the live list is unavailable. */
export const AKAHU_PAY_BY_BANK_BANKS: AkahuSupportedBank[] = [
  { name: "ANZ" },
  { name: "ASB" },
  { name: "BNZ" },
  { name: "Kiwibank" },
  { name: "NZ Home Loans" },
  { name: "Westpac" },
];

/** Names used to match / filter live Akahu connection rows. */
export const AKAHU_PAY_BY_BANK_BANK_NAMES = AKAHU_PAY_BY_BANK_BANKS.map((bank) => bank.name);

const AKAHU_BANK_DISPLAY_ALIASES: Record<string, string> = {
  nzhl: "NZ Home Loans",
  "nz home loans": "NZ Home Loans",
};

export function normalizeAkahuBankName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/^the\s+/, "")
    .replace(/_/g, " ");
}

export function canonicalAkahuBankName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "";
  return AKAHU_BANK_DISPLAY_ALIASES[normalizeAkahuBankName(trimmed)] ?? trimmed;
}

export function isAkahuDemoBank(name: string): boolean {
  const normalized = normalizeAkahuBankName(name);
  return normalized === "demo bank" || normalized === "demobank";
}

export function isAkahuPayByBankName(name: string): boolean {
  const canonical = canonicalAkahuBankName(name);
  if (!canonical) return false;
  const normalized = normalizeAkahuBankName(canonical);
  return AKAHU_PAY_BY_BANK_BANK_NAMES.some((supported) => normalizeAkahuBankName(supported) === normalized);
}

/**
 * Live payments-API connections become the shown list (demo bank dropped).
 * Empty or unusable live data falls back to the official static names.
 */
export function mergeAkahuSupportedBanks(
  connections: Array<{ name: string; logoUrl?: string | null; connectionType?: string | null }>,
): AkahuSupportedBank[] {
  const byName = new Map<string, AkahuSupportedBank & { connectionType?: string | null }>();

  for (const connection of connections) {
    if (isAkahuDemoBank(connection.name)) {
      continue;
    }
    const displayName = canonicalAkahuBankName(connection.name);
    if (!displayName) {
      continue;
    }
    const key = normalizeAkahuBankName(displayName);
    const existing = byName.get(key);
    const next = {
      name: displayName,
      logoUrl: connection.logoUrl ?? null,
      connectionType: connection.connectionType ?? null,
    };

    if (!existing) {
      byName.set(key, next);
      continue;
    }

    const preferNext = existing.connectionType !== "official" && next.connectionType === "official";
    if (preferNext || (!existing.logoUrl && next.logoUrl)) {
      byName.set(key, {
        ...existing,
        ...next,
        name: existing.name.length >= next.name.length ? existing.name : next.name,
      });
    }
  }

  if (byName.size === 0) {
    return AKAHU_PAY_BY_BANK_BANKS.map((fallback) => ({
      name: fallback.name,
      logoUrl: null,
    }));
  }

  const used = new Set<string>();
  const ordered: AkahuSupportedBank[] = [];

  for (const fallback of AKAHU_PAY_BY_BANK_BANKS) {
    const key = normalizeAkahuBankName(fallback.name);
    const live = byName.get(key);
    if (!live) {
      continue;
    }
    used.add(key);
    ordered.push({
      name: fallback.name,
      logoUrl: live.logoUrl ?? null,
    });
  }

  for (const [key, live] of byName) {
    if (used.has(key)) {
      continue;
    }
    ordered.push({
      name: live.name,
      logoUrl: live.logoUrl ?? null,
    });
  }

  return ordered;
}
